"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OPTIONAL_BUILDER_SECTIONS = exports.JOB_BUILDER_SECTION_LABELS = exports.JOB_BUILDER_SECTIONS = exports.updateJobMatrixSchema = exports.matrixLineSchema = exports.matrixAnchorsSchema = exports.MAX_MATRIX_LINES = exports.MATRIX_SCALE_MAX = exports.ANCHOR_POINTS = exports.MATRIX_USE_LABELS = exports.MATRIX_USES = exports.JOB_FILE_MAX_BYTES = exports.JOB_IMAGE_EXTENSIONS = exports.JOB_FILE_EXTENSIONS = exports.JOB_DOCUMENT_TYPES = exports.updateJobIdealProfileSchema = exports.updateJobBriefSchema = exports.briefSchema = exports.briefSectionSchema = exports.briefSectionKeySchema = exports.DEFAULT_BRIEF_SECTIONS = exports.updateJobCriteriaSchema = exports.configCriterionSchema = exports.builderCriterionSchema = exports.IMPORTANCE_PARTS = exports.CRITERION_KIND_LABELS = exports.CRITERION_IMPORTANCE_LABELS = exports.MAX_BUILDER_CRITERIA = void 0;
exports.importanceWeights = importanceWeights;
exports.classFromImportance = classFromImportance;
exports.kindFromLegacyType = kindFromLegacyType;
exports.legacyTypeFromKind = legacyTypeFromKind;
exports.minimumRange = minimumRange;
exports.defaultMinimum = defaultMinimum;
exports.legacyShapeOf = legacyShapeOf;
exports.deriveLegacyCriteria = deriveLegacyCriteria;
exports.builderCriterionFromLegacy = builderCriterionFromLegacy;
exports.defaultBrief = defaultBrief;
exports.hasBriefContent = hasBriefContent;
exports.parseBulletMarkup = parseBulletMarkup;
exports.jobFileProblem = jobFileProblem;
exports.roundUse = roundUse;
exports.matrixLinesFor = matrixLinesFor;
exports.cleanAnchors = cleanAnchors;
exports.followsLineManager = followsLineManager;
exports.jobSectionStates = jobSectionStates;
exports.openBlockers = openBlockers;
exports.sectionForWizardStep = sectionForWizardStep;
exports.isJobBuilderSection = isJobBuilderSection;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const hiring_templates_1 = require("./hiring-templates");
const schemas_1 = require("./schemas");
// R3 — the one-page job builder (JOB-13; F5.19–F5.22). Sources: master plan R3; PRD §4.1
// (JB-1…JB-18); §L30 "Weights from importance"; D22; DECISIONS-LOG "Start R3".
//
// A criterion is now picked by importance and kind. §L1's class, weight and type are
// derived from those on save, so `computeMatchScore` and every §L1 caller keep working
// unchanged until R5 extends the scorer (handoff R3, data model).
exports.MAX_BUILDER_CRITERIA = 20;
const LABEL_MAX = 120;
const LOOK_FOR_MAX = 20;
const LOOK_FOR_WORD_MAX = 60;
const LEVEL_MIN = 1;
const LEVEL_MAX = 5;
const YEARS_MAX = 50;
exports.CRITERION_IMPORTANCE_LABELS = {
    must_have: 'Must-have',
    important: 'Important',
    nice_to_have: 'Nice to have',
};
exports.CRITERION_KIND_LABELS = {
    yes_no: 'Yes/No',
    level: 'Level 1–5',
    years: 'Years',
    keywords: 'Keywords',
    mentioned: 'Mentioned in CV',
};
/** §L30: Must-have 3 · Important 2 · Nice to have 1. Static for now (O24). */
exports.IMPORTANCE_PARTS = {
    must_have: 3,
    important: 2,
    nice_to_have: 1,
};
const WEIGHT_TOTAL = 100;
/**
 * §L30: `w_i = 100 × p_i / Σp`, rounded by the largest remainder so the total is exactly
 * 100. Ties go to the earlier row. Nobody types a weight anywhere (O24).
 */
function importanceWeights(importances) {
    if (importances.length === 0)
        return [];
    const parts = importances.map((importance) => exports.IMPORTANCE_PARTS[importance]);
    const totalParts = parts.reduce((sum, part) => sum + part, 0);
    const exact = parts.map((part) => (WEIGHT_TOTAL * part) / totalParts);
    const weights = exact.map(Math.floor);
    const leftOver = WEIGHT_TOTAL - weights.reduce((sum, weight) => sum + weight, 0);
    const byRemainder = exact
        .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
        // Stable sort keeps the earlier row first on a tie.
        .sort((a, b) => b.remainder - a.remainder);
    for (const { index } of byRemainder.slice(0, leftOver))
        weights[index] = (weights[index] ?? 0) + 1;
    return weights;
}
/** §L30: Must-have is §L1's `must_have`; Important and Nice to have are `nice_to_have`. */
function classFromImportance(importance) {
    return importance === 'must_have' ? 'must_have' : 'nice_to_have';
}
/** Master plan R3: how each old criterion type reads in the builder. */
function kindFromLegacyType(type) {
    switch (type) {
        case 'skill':
        case 'location':
            return 'keywords';
        case 'experience_years':
            return 'years';
        case 'education_level':
        case 'certification':
            return 'yes_no';
        case 'language':
            return 'level';
    }
}
/** §L30's "§L1 type" column: what a new criterion of each kind is scored as until R5. */
function legacyTypeFromKind(kind) {
    switch (kind) {
        case 'yes_no':
            return 'certification';
        case 'years':
            return 'experience_years';
        case 'level':
        case 'keywords':
        case 'mentioned':
            return 'skill';
    }
}
/** The kinds that carry a minimum, and its range. */
function minimumRange(kind) {
    if (kind === 'level')
        return { min: LEVEL_MIN, max: LEVEL_MAX };
    if (kind === 'years')
        return { min: 0, max: YEARS_MAX };
    return null;
}
const DEFAULT_MINIMUM = { level: 3, years: 2 };
function defaultMinimum(kind) {
    return DEFAULT_MINIMUM[kind] ?? null;
}
const lookForSchema = zod_1.z
    .array(zod_1.z.string().trim().min(1).max(LOOK_FOR_WORD_MAX))
    .max(LOOK_FOR_MAX, `At most ${LOOK_FOR_MAX} words.`);
const builderCriterionShape = {
    /** The row being edited; a new row carries the id the browser gave it, so the next save updates it. */
    id: schemas_1.uuidSchema.optional(),
    label: zod_1.z.string().trim().min(1, 'Say what you want.').max(LABEL_MAX),
    importance: zod_1.z.enum(enums_1.CRITERION_IMPORTANCES),
    kind: zod_1.z.enum(enums_1.CRITERION_KINDS),
    lookFor: lookForSchema.default([]),
    /** §L30's synonyms, counting 0.7 (DECISIONS-LOG "Start R5" Q2). */
    partlyCounts: lookForSchema.default([]),
    minLevel: zod_1.z.number().int('Whole numbers only.').nullable().default(null),
};
function checkMinimum(criterion, ctx) {
    const range = minimumRange(criterion.kind);
    if (!range || criterion.minLevel === null)
        return;
    if (criterion.minLevel < range.min || criterion.minLevel > range.max) {
        ctx.addIssue({ code: 'custom', path: ['minLevel'], message: `Between ${range.min} and ${range.max}.` });
    }
}
/** One row of the criteria table. The row's `key` is the server's and never changes (R5's corrections hang on it). */
exports.builderCriterionSchema = zod_1.z.object(builderCriterionShape).superRefine(checkMinimum);
const legacyShapeSchema = zod_1.z.object({
    type: zod_1.z.enum(enums_1.CRITERION_TYPES),
    params: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
});
/**
 * A criterion as a template or a config version stores it: the builder row plus the §L1
 * type it was scored as, so a copy keeps an old "education level" scored as education.
 * A template saved before R3 holds the old shape, and is read as a builder row.
 */
exports.configCriterionSchema = zod_1.z.preprocess(upgradeLegacyCriterion, zod_1.z.object({ ...builderCriterionShape, legacy: legacyShapeSchema.nullable().default(null) }).superRefine(checkMinimum));
function upgradeLegacyCriterion(value) {
    if (!isRecord(value) || 'kind' in value)
        return value;
    const type = enums_1.CRITERION_TYPES.find((candidate) => candidate === value['type']);
    const params = isRecord(value['params']) ? value['params'] : {};
    if (!type || typeof value['label'] !== 'string')
        return value;
    const criterionClass = value['class'] === 'must_have' ? 'must_have' : 'nice_to_have';
    return { ...builderCriterionFromLegacy({ type, class: criterionClass, label: value['label'], params }), legacy: { type, params } };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/** F5.21. Weights are never sent: the server works them out (O24). */
exports.updateJobCriteriaSchema = schemas_1.versionedSchema.extend({
    criteria: zod_1.z.array(exports.builderCriterionSchema).max(exports.MAX_BUILDER_CRITERIA, `At most ${exports.MAX_BUILDER_CRITERIA} criteria.`),
});
/**
 * The §L1 type and params for a criterion. A row that already has a type of the same kind
 * keeps it, so an old "education level: Master's" stays scored as education until R5;
 * otherwise the kind decides. Look-for words become §L1's synonyms, and a Years minimum
 * its `min_years`.
 */
function legacyShapeOf(criterion, previous) {
    const isSameKind = previous !== null && kindFromLegacyType(previous.type) === criterion.kind;
    const type = isSameKind ? previous.type : legacyTypeFromKind(criterion.kind);
    const kept = isSameKind ? previous.params : {};
    switch (type) {
        case 'skill':
            return { type, params: { ...kept, synonyms: [...criterion.lookFor] } };
        case 'experience_years':
            return { type, params: { ...kept, min_years: criterion.minLevel ?? 0 } };
        case 'education_level':
        case 'certification':
        case 'language':
        case 'location':
            return { type, params: kept };
    }
}
/** Every row's §L1 fields, with the weights worked out over the whole table. */
function deriveLegacyCriteria(criteria, previous) {
    const weights = importanceWeights(criteria.map((criterion) => criterion.importance));
    return criteria.map((criterion, index) => ({
        ...legacyShapeOf(criterion, previous[index] ?? null),
        class: classFromImportance(criterion.importance),
        weight: weights[index] ?? 0,
    }));
}
/** An old-style criterion (a template saved before R3) read as a builder row. */
function builderCriterionFromLegacy(legacy) {
    const kind = kindFromLegacyType(legacy.type);
    const synonyms = legacy.params['synonyms'];
    const minYears = legacy.params['min_years'];
    return {
        label: legacy.label,
        importance: legacy.class === 'must_have' ? 'must_have' : 'nice_to_have',
        kind,
        lookFor: Array.isArray(synonyms) ? synonyms.filter((word) => typeof word === 'string') : [],
        partlyCounts: [],
        minLevel: kind === 'years' && typeof minYears === 'number' ? minYears : defaultMinimum(kind),
    };
}
// --- The brief (F5.19, JB-5…JB-7) ---------------------------------------------------------
const MAX_BRIEF_SECTIONS = 12;
const MAX_BULLETS = 40;
const BULLET_MAX = 500;
const SECTION_TITLE_MAX = 80;
/** JB-6 and F5.19: four starting sections, each optional; the recruiter adds, renames, reorders. */
exports.DEFAULT_BRIEF_SECTIONS = [
    { key: 'why', title: 'Why we need this role' },
    { key: 'achieve', title: 'What this person will achieve' },
    { key: 'plan_30', title: '30-day plan' },
    { key: 'plan_90', title: '90-day plan' },
];
exports.briefSectionKeySchema = zod_1.z
    .string()
    .regex(/^[a-z0-9_-]{1,40}$/, 'That section reference is not valid.');
exports.briefSectionSchema = zod_1.z.object({
    key: exports.briefSectionKeySchema,
    title: zod_1.z.string().trim().min(1, 'Give the section a title.').max(SECTION_TITLE_MAX),
    // Empty bullets are kept: a new line is empty while it's being typed, and dropping it on
    // save would make the line vanish under the cursor (CLAUDE.md §5a rule 3).
    bullets: zod_1.z.array(zod_1.z.string().max(BULLET_MAX)).max(MAX_BULLETS, `At most ${MAX_BULLETS} bullets in a section.`),
});
exports.briefSchema = zod_1.z
    .array(exports.briefSectionSchema)
    .max(MAX_BRIEF_SECTIONS, `At most ${MAX_BRIEF_SECTIONS} sections.`)
    .refine((sections) => new Set(sections.map((section) => section.key)).size === sections.length, 'Two sections share a reference.');
exports.updateJobBriefSchema = schemas_1.versionedSchema.extend({ brief: exports.briefSchema });
/** F5.31 (ADR 0092): HR edits and completes the job's Ideal candidate profile, the eight boxes copied from the hire request. */
exports.updateJobIdealProfileSchema = schemas_1.versionedSchema.extend({ idealProfile: hiring_templates_1.idealProfileSchema });
function defaultBrief() {
    return exports.DEFAULT_BRIEF_SECTIONS.map((section) => ({ ...section, bullets: [] }));
}
function hasBriefContent(brief) {
    return brief.some((section) => section.bullets.some((bullet) => bullet.trim() !== ''));
}
// `**bold**` or `[text](https://…)`. Only http(s) and mailto links are turned into links, so a
// `javascript:` address stays plain text.
const MARKUP = /\*\*(.+?)\*\*|\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g;
/** Splits a bullet into plain, bold and link pieces. The web renders them as elements, never as HTML. */
function parseBulletMarkup(text) {
    const segments = [];
    let last = 0;
    for (const match of text.matchAll(MARKUP)) {
        const start = match.index;
        if (start > last)
            segments.push({ kind: 'text', text: text.slice(last, start) });
        const [whole, bold, linkText, href] = match;
        if (bold !== undefined)
            segments.push({ kind: 'bold', text: bold });
        else if (linkText !== undefined && href !== undefined)
            segments.push({ kind: 'link', text: linkText, href });
        last = start + whole.length;
    }
    if (last < text.length)
        segments.push({ kind: 'text', text: text.slice(last) });
    return segments;
}
// --- Job files (F5.20, JB-3, JB-7) -----------------------------------------------------------
exports.JOB_DOCUMENT_TYPES = ['job_file', 'job_image'];
/** §L26 document rules for a job: PDF, Word or an image. */
exports.JOB_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg'];
exports.JOB_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'];
exports.JOB_FILE_MAX_BYTES = 10 * 1024 * 1024;
function jobFileProblem(file, documentType) {
    const extension = file.fileName.split('.').pop()?.toLowerCase() ?? '';
    const allowed = documentType === 'job_image' ? exports.JOB_IMAGE_EXTENSIONS : exports.JOB_FILE_EXTENSIONS;
    if (!allowed.includes(extension)) {
        return documentType === 'job_image' ? 'Choose a PNG or JPG image.' : 'Attach a PDF, Word file or image.';
    }
    if (file.sizeBytes > exports.JOB_FILE_MAX_BYTES)
        return 'That file is over 10 MB.';
    if (file.sizeBytes === 0)
        return 'That file is empty.';
    return null;
}
// --- The scoring matrix (F5.22, JB-11…JB-14) --------------------------------------------------
/** Where a matrix line is used (JB-13): HR review (R6) and each interview round. */
exports.MATRIX_USES = ['hr_review', 'round_1', 'round_2', 'round_3'];
exports.MATRIX_USE_LABELS = {
    hr_review: 'HR review',
    round_1: 'Round 1',
    round_2: 'Round 2',
    round_3: 'Round 3',
};
/** JB-12 and F5.22: descriptions for what a 1, a 3 and a 5 look like. */
exports.ANCHOR_POINTS = ['1', '3', '5'];
exports.MATRIX_SCALE_MAX = 5;
exports.MAX_MATRIX_LINES = 20;
const ANCHOR_MAX = 200;
exports.matrixAnchorsSchema = zod_1.z.object({
    '1': zod_1.z.string().trim().max(ANCHOR_MAX).optional(),
    '3': zod_1.z.string().trim().max(ANCHOR_MAX).optional(),
    '5': zod_1.z.string().trim().max(ANCHOR_MAX).optional(),
});
exports.matrixLineSchema = zod_1.z.object({
    /** The line across every version and every round's rubric. Absent on a new line. */
    key: schemas_1.uuidSchema.optional(),
    label: zod_1.z.string().trim().min(1, 'Name the quality.').max(LABEL_MAX),
    anchors: exports.matrixAnchorsSchema.default({}),
    /** §L3 (DECISIONS-LOG "Start R3" Q4): a score under the floor blocks advance. Off by default. */
    isCritical: zod_1.z.boolean().default(false),
    usedIn: zod_1.z
        .array(zod_1.z.enum(exports.MATRIX_USES))
        .default([])
        .refine((uses) => new Set(uses).size === uses.length, 'A use is ticked twice.'),
});
exports.updateJobMatrixSchema = schemas_1.versionedSchema.extend({
    lines: zod_1.z.array(exports.matrixLineSchema).max(exports.MAX_MATRIX_LINES, `At most ${exports.MAX_MATRIX_LINES} lines.`),
});
function roundUse(roundNumber) {
    const use = `round_${roundNumber}`;
    return exports.MATRIX_USES.find((candidate) => candidate === use) ?? null;
}
/** JB-14: the lines one round's scorecard is made from. */
function matrixLinesFor(lines, roundNumber) {
    const use = roundUse(roundNumber);
    return use ? lines.filter((line) => line.usedIn.includes(use)) : [];
}
/** Non-empty anchors only, so an emptied description doesn't linger as "". */
function cleanAnchors(anchors) {
    return Object.fromEntries(exports.ANCHOR_POINTS.flatMap((point) => (anchors[point] ? [[point, anchors[point]]] : [])));
}
// --- Sections and their ticks (JOB-13) ---------------------------------------------------------
/**
 * JOB-13's sections, down the left. Form and Offer show only with their module on (§L34); Screening criteria only with
 * `screening_criteria` on, and "Ideal candidate" for a job ranked by the profile (ADR 0062).
 */
exports.JOB_BUILDER_SECTIONS = ['basics', 'brief', 'profile', 'criteria', 'matrix', 'stages', 'form', 'offer', 'post'];
exports.JOB_BUILDER_SECTION_LABELS = {
    basics: 'Basics',
    brief: 'Brief',
    profile: 'Ideal candidate',
    criteria: 'Screening criteria',
    matrix: 'Scoring matrix',
    stages: 'Stages',
    form: 'Application form',
    offer: 'Offer',
    post: 'Job post',
};
/** ADR 0066: sections that never block "Open job"; their tick only says whether they're done. */
exports.OPTIONAL_BUILDER_SECTIONS = ['post', 'matrix'];
/**
 * DECISIONS-LOG "Start R3" Q2: a job's hiring manager follows its line manager while it is empty
 * or still the same person; once someone else is chosen it stays theirs. The builder and the
 * server both ask this, so the row on screen and the saved job never disagree.
 */
function followsLineManager(people) {
    return people.hiringManagerUserId === null || people.hiringManagerUserId === people.lineManagerUserId;
}
/** Each section's tick, and what it still needs (handoff R3, build order 1). */
function jobSectionStates(rules) {
    const basics = [];
    if (!rules.basics.title.trim())
        basics.push('A job title');
    if (rules.basics.slotsTotal < 1)
        basics.push('How many people are needed');
    if (!rules.basics.recruiterUserId)
        basics.push('A recruiter');
    // JB-2, D20: with no hire request behind the job, these two get the shortlist (§L31).
    if (!rules.basics.requestedByUserId)
        basics.push('Who requested this job');
    if (!rules.basics.lineManagerUserId)
        basics.push('The line manager');
    if (rules.basics.shiftStart !== null && (rules.basics.shiftEnd === null || rules.basics.shiftDisplayZone === null)) {
        basics.push('The shift end time and the time zone to show it in');
    }
    const brief = hasBriefContent(rules.brief) ? [] : ['At least one bullet in any section'];
    const profile = [];
    if (rules.rankedBy === 'profile') {
        if (rules.profile === null || rules.profile.lines === 0)
            profile.push('Add the ideal candidate');
        else if (rules.profile.musts === 0)
            profile.push('Rate at least one line 5 (Must have)');
    }
    const criteria = [];
    if (rules.criteria.length === 0)
        criteria.push('At least one criterion');
    else if (!rules.criteria.some((criterion) => criterion.importance === 'must_have')) {
        criteria.push('At least one Must-have');
    }
    const stages = [...rules.stageProblems];
    const form = rules.isFormOn ? [...rules.formProblems] : [];
    const offer = rules.isOfferOn ? [...rules.offerProblems] : [];
    const state = (section, missing, isShown = true) => ({
        section,
        isComplete: missing.length === 0,
        missing,
        isShown,
    });
    return [
        state('basics', basics),
        state('brief', brief),
        state('profile', profile, rules.rankedBy === 'profile'),
        // A job ranked by the profile is blocked by its profile, never by criteria (ADR 0062 §3); with the module off the section is hidden.
        state('criteria', rules.rankedBy === 'profile' ? [] : criteria, rules.isCriteriaOn),
        // Owner, 2026-11-01 (DECISIONS-LOG "Job header numbers…" Q3 a): the scoring matrix is no part of making a job. It lives
        // on each Interview step, so a job with no interview has none. ADR 0077: it never blocked opening and still doesn't.
        state('matrix', [], false),
        state('stages', stages),
        state('form', form, rules.isFormOn),
        state('offer', offer, rules.isOfferOn),
        state('post', rules.hasPostImage ? [] : ['Approve the post words to make the image, or upload your own'], rules.isPostOn),
    ];
}
/** What still blocks "Open job": every shown section that isn't complete. */
function openBlockers(sections) {
    return sections.filter((state) => state.isShown && !state.isComplete && !exports.OPTIONAL_BUILDER_SECTIONS.includes(state.section));
}
/** Old wizard addresses (`/setup/criteria`…) still land: old notifications point at them. */
function sectionForWizardStep(step) {
    switch (step) {
        case 'profile':
        case 'criteria':
        case 'form':
        case 'offer':
        case 'basics':
            return step;
        case 'pipeline':
        case 'assessment':
        case 'interview':
            return 'stages';
        default:
            return 'basics';
    }
}
function isJobBuilderSection(value) {
    return exports.JOB_BUILDER_SECTIONS.includes(value);
}
//# sourceMappingURL=job-builder.js.map